import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../utils/api";
import { HiOutlineEye } from "react-icons/hi";
import Modal from "../components/Modal";
import AddContentOwner from "../components/contentOwner/AddContentOwner";
import ViewContentOwner from "../components/contentOwner/ViewContentOwner";
import EditContentOwner from "../components/contentOwner/EditContentOwner";
import AddBtn from "../components/AddBtn";
import EditBtn from "../components/EditBtn";
import DeleteBtn from "../components/DeleteBtn";
import RoleAccess from "../components/RoleAccess";

export default function ContentOwner() {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [contentOwners, setContentOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const itemsPerPage = 5;

  const [isModalOpenForAdd, setIsModalOpenForAdd] = useState(false);
  const [isModalOpenForView, setIsModalOpenForView] = useState(false);
  const [isModalOpenForEdit, setIsModalOpenForEdit] = useState(false);
  const [selectedContentOwner, setSelectedContentOwner] = useState(null);

  useEffect(() => {
    if (!productId) return;

    api
      .get(`/products/${productId}`, { withCredentials: true })
      .then((res) => setProduct(res.data))
      .catch((err) => console.error("Error fetching product:", err));
  }, [productId]);

  useEffect(() => {
    api
      .get("/users/me", { withCredentials: true })
      .then((res) => setUserRole(res.data.role))
      .catch(() => setUserRole(null));
  }, []);

  const fetchContentOwners = async (page = 1, q = "") => {
    setLoading(true);
    try {
      const res = await api.get(`/contentOwners/products/${productId}`, {
        params: { page, limit: itemsPerPage, q },
        withCredentials: true,
      });

      setContentOwners(res.data.contentOwners || res.data || []);

      if (res.data.pagination) {
        setTotalPages(res.data.pagination.totalPages || 1);
      }
    } catch (err) {
      console.error("Error fetching content owners:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!productId) return;
    fetchContentOwners(currentPage, search);
  }, [productId, currentPage, search]);

  const deleteContentOwner = async (id) => {
    if (!window.confirm("Are you sure you want to delete this?")) return;

    try {
      await api.delete(`/contentOwners/${id}`, { withCredentials: true });

      if (contentOwners.length === 1 && currentPage > 1) {
        setCurrentPage((p) => p - 1);
      } else {
        fetchContentOwners(currentPage, search);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to delete content owner!");
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setSearch(searchInput);
  };

  const handleClear = () => {
    setSearchInput("");
    setCurrentPage(1);
    setSearch("");
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <>
      <Modal
        isOpen={isModalOpenForAdd}
        onClose={() => setIsModalOpenForAdd(false)}
      >
        <AddContentOwner
          productId={productId}
          onSuccess={() => {
            setIsModalOpenForAdd(false);
            setCurrentPage(1);
            fetchContentOwners(1, search);
          }}
        />
      </Modal>

      <Modal
        isOpen={isModalOpenForView}
        onClose={() => setIsModalOpenForView(false)}
      >
        <ViewContentOwner contentOwner={selectedContentOwner} />
      </Modal>

      <Modal
        isOpen={isModalOpenForEdit}
        onClose={() => setIsModalOpenForEdit(false)}
      >
        {selectedContentOwner && (
          <EditContentOwner
            contentOwner={selectedContentOwner}
            onSuccess={() => {
              setIsModalOpenForEdit(false);
              fetchContentOwners(currentPage, search);
            }}
          />
        )}
      </Modal>

      <div className="flex flex-col items-center mt-10 gap-2">
        {product && (
          <>
            <img
              src={`${process.env.REACT_APP_API_URL}/${product.imageUrl}`}
              alt={product.name}
              className="w-14 h-14 object-cover rounded-xl"
            />
            <h1 className="text-2xl font-bold">{product.name}</h1>
            <h2 className="text-xl font-semibold">Content Owners</h2>
          </>
        )}
      </div>

      <div className="ml-2 mt-5 bg-white rounded-md shadow-lg p-5">
        <RoleAccess allow={["Admin", "Manager"]} role={userRole}>
          <div className="mb-4">
            <AddBtn
              onClick={() => setIsModalOpenForAdd(true)}
              label="Add Content Owner"
            />
          </div>
        </RoleAccess>
        <div className="mb-4 flex justify-end gap-2">
          <input
            type="text"
            placeholder="Search by artist name, name, mobile, or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            className="w-[450px] px-4 py-2 border rounded-lg"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition"
          >
            Search
          </button>
          <button
            onClick={handleClear}
            className="px-4 py-2 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 transition"
          >
            Clear
          </button>
        </div>

        <table className="w-full text-sm border-separate border-spacing-y-2">
          <thead>
            <tr className="bg-slate-100 text-slate-600">
              <th className="px-4 py-3 text-left rounded-tl-xl rounded-bl-xl w-1/6">
                Artist Name
              </th>
              <th className="px-4 py-3 text-center">Name</th>
              <th className="px-4 py-3 text-center">Mobile</th>
              <th className="px-4 py-3 text-center">Email</th>
              <th className="px-4 py-3 text-right rounded-tr-xl rounded-br-xl">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  Loading...
                </td>
              </tr>
            ) : contentOwners.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  No content owners found.
                </td>
              </tr>
            ) : (
              contentOwners.map((owner) => (
                <tr
                  key={owner._id}
                  className="cursor-pointer hover:bg-slate-100"
                  onClick={() =>
                    navigate(`/dashboard/content-owner/${owner._id}/contracts`)
                  }
                >
                  <td className="px-4 py-4">{owner.artistName}</td>
                  <td className="px-4 py-4 text-center">{owner.name}</td>
                  <td className="px-4 py-4 text-center">{owner.mobile}</td>
                  <td className="px-4 py-4 text-center">{owner.email}</td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedContentOwner(owner);
                          setIsModalOpenForView(true);
                        }}
                      >
                        <HiOutlineEye className="h-5 w-5 text-green-400 hover:text-green-700 transition transform hover:scale-110" />
                      </button>
                      <RoleAccess allow={["Admin", "Manager"]} role={userRole}>
                        <EditBtn
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedContentOwner(owner);
                            setIsModalOpenForEdit(true);
                          }}
                          label="Edit Content Owner"
                        />
                        <DeleteBtn
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteContentOwner(owner._id);
                          }}
                          label="Delete Content Owner"
                        />
                      </RoleAccess>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Prev
            </button>

            <span className="font-medium">
              {currentPage} / {totalPages}
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
}
